import { UserProfile, BusinessProfile } from '../types/profile';

// Mock current user profile (matches the user from AuthContext after onboarding)
export const mockCurrentUserProfile: UserProfile = {
  userId: 'user-1',
  name: 'John Doe',
  profession: 'Software Engineer',
  industry: 'Technology',
  city: 'San Francisco',
  profilePhotoUrl: 'https://img.freepik.com/free-photo/portrait-white-man-isolated_53876-40306.jpg',
  bio: 'Passionate software engineer with 5+ years of experience building mobile and web applications. Love creating user-friendly solutions.',
  skills: ['React Native', 'TypeScript', 'Node.js', 'UI/UX Design'],
  experienceLevel: 'Expert',
  languages: ['English', 'Spanish'],
  availability: 'Freelance',
  website: 'https://johndoe.dev',
  businessId: 'business-1',
};

// Mock business profile for current user
export const mockCurrentUserBusiness: BusinessProfile = {
  businessId: 'business-1',
  ownerId: 'user-1',
  name: 'TechSolutions Inc',
  category: 'Technology',
  description: 'We build innovative mobile and web applications for startups and enterprises. Specializing in React Native, modern web technologies, and cloud solutions.',
  city: 'San Francisco',
  yearsOfExperience: 5,
  website: 'https://techsolutions.com',
  contactEmail: 'contact@techsolutions.com',
};

// Mock profiles for other users
export const mockUserProfiles: Record<string, UserProfile> = {
  'user-2': {
    userId: 'user-2',
    name: 'Jane Smith',
    profession: 'Product Designer',
    industry: 'Design',
    city: 'New York',
    bio: 'Creative product designer focused on user-centered design and creating delightful experiences.',
    skills: ['Figma', 'User Research', 'Prototyping', 'Design Systems'],
    experienceLevel: 'Expert',
    languages: ['English'],
    availability: 'Full-time',
    website: 'https://janesmith.design',
  },
  'user-3': {
    userId: 'user-3',
    name: 'Bob Johnson',
    profession: 'Marketing Manager',
    industry: 'Marketing',
    city: 'Chicago',
    bio: 'Digital marketing expert with a passion for data-driven strategies and brand growth.',
    skills: ['SEO', 'Content Marketing', 'Analytics', 'Social Media'],
    experienceLevel: 'Intermediate',
    languages: ['English', 'French'],
    availability: 'Full-time',
    businessId: 'business-3',
  },
  'user-4': {
    userId: 'user-4',
    name: 'Alice Williams',
    profession: 'Data Scientist',
    industry: 'Technology',
    city: 'Seattle',
    bio: 'Data scientist specializing in machine learning and predictive analytics.',
    skills: ['Python', 'Machine Learning', 'SQL', 'Data Visualization'],
    experienceLevel: 'Expert',
    languages: ['English', 'Mandarin'],
    availability: 'Full-time',
  },
  'user-5': {
    userId: 'user-5',
    name: 'Charlie Brown',
    profession: 'UX Designer',
    industry: 'Design',
    city: 'Austin',
    bio: 'UX designer passionate about creating intuitive and accessible digital experiences.',
    skills: ['User Research', 'Wireframing', 'Usability Testing', 'Sketch'],
    experienceLevel: 'Intermediate',
    languages: ['English'],
    availability: 'Part-time',
  },
};

// Mock business profiles for other users
export const mockBusinessProfiles: Record<string, BusinessProfile> = {
  'business-3': {
    businessId: 'business-3',
    ownerId: 'user-3',
    name: 'Growth Marketing Co',
    category: 'Marketing',
    description: 'Full-service digital marketing agency helping businesses grow their online presence and drive results.',
    city: 'Chicago',
    yearsOfExperience: 8,
    website: 'https://growthmarketing.co',
    contactEmail: 'hello@growthmarketing.co',
  },
};

// Helper functions
export const getUserProfile = (userId: string): UserProfile | undefined => {
  if (userId === 'user-1') {
    return mockCurrentUserProfile;
  }
  return mockUserProfiles[userId];
};

export const getBusinessProfile = (businessId: string): BusinessProfile | undefined => {
  if (businessId === 'business-1') {
    return mockCurrentUserBusiness;
  }
  return mockBusinessProfiles[businessId];
};

export const updateUserProfile = (userId: string, updates: Partial<UserProfile>): void => {
  if (userId === 'user-1') {
    Object.assign(mockCurrentUserProfile, updates);
  } else if (mockUserProfiles[userId]) {
    Object.assign(mockUserProfiles[userId], updates);
  }
};

export const createBusiness = (ownerId: string, businessData: Omit<BusinessProfile, 'businessId' | 'ownerId'>): BusinessProfile => {
  const businessId = `business-${Date.now()}`;
  const newBusiness: BusinessProfile = {
    businessId,
    ownerId,
    ...businessData,
  };
  
  mockBusinessProfiles[businessId] = newBusiness;
  
  // Update user profile with business ID
  if (ownerId === 'user-1') {
    mockCurrentUserProfile.businessId = businessId;
  } else if (mockUserProfiles[ownerId]) {
    mockUserProfiles[ownerId].businessId = businessId;
  }
  
  return newBusiness;
};

export const updateBusiness = (businessId: string, updates: Partial<BusinessProfile>): void => {
  if (businessId === 'business-1') {
    Object.assign(mockCurrentUserBusiness, updates);
  } else if (mockBusinessProfiles[businessId]) {
    Object.assign(mockBusinessProfiles[businessId], updates);
  }
};

export const deleteBusiness = (businessId: string, ownerId: string): void => {
  // Remove business
  if (businessId === 'business-1') {
    // Can't delete via mock for current user's business, but in real app would remove
    mockCurrentUserProfile.businessId = undefined;
  } else {
    delete mockBusinessProfiles[businessId];
    
    // Update user profile
    if (mockUserProfiles[ownerId]) {
      mockUserProfiles[ownerId].businessId = undefined;
    }
  }
};
